import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField } from '@mui/material'
import { useEffect, useMemo } from 'react';
import { formatarCEP, formatarCNPJ, formatarIncra, formatarNirf } from '../../../../../helpers/masks';
import moment, { Moment } from 'moment';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import { useQuery } from 'react-query';
import axiosClient from '../../../../../lib/axios';
import { ViaCEPResposta } from '../../../../../types/externas/viacep-resultado';
import SearchIcon from '@mui/icons-material/Search';
import { desformatarCEP, desformatarCPFCNPJ } from '../../../../../helpers/unmask';
import { validarCpfCnpj } from '../../../../../helpers/verificadores';

export default function DialogAddEditFazenda({ open, index, onClose }: DialogAddEditFazendaProps) {

    const isEdit = typeof index === 'number';
    const { setValue: setMainFormValue, getValues: getMainFormValues } = useFormContext();

    const ufs = useMemo(() => ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"], [])

    const opcoesRadio = useMemo(() => [
        { value: "Sim", label: "Sim" },
        { value: "Não", label: "Não" },
    ], [])

    const opcoesNatureza = useMemo(() => [
        { value: "1", label: "Proprietário" },
        { value: "2", label: "Posseiro" },
        { value: "3", label: "Arrendatário" },
        { value: "4", label: "Herdeiro de Espólio" },
        { value: "5", label: "Proprietário em Condomínio" },
        { value: "6", label: "Locatário com Contrato" },
        { value: "7", label: "Não Possui" },
    ], []);

    const { control, handleSubmit, reset, watch, getValues, setValue } = useForm<IFazendaForm>({
        defaultValues: defaultValuesFazenda,
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
    });

    const tributaIcms = watch('tributa_icms') === "Sim";
    const possuiArendatarioComodatario = watch('arrendatario') === "Sim";

    //queries
    const { data } = useQuery({
        queryKey: ["pesquisar-cidades", watch(`uf`)],
        queryFn: async () => {
            if (watch(`uf`) === undefined || watch(`uf`) === "") return []
            let result = await axiosClient.get("/pesquisar-cidades", {
                params: {
                    uf: getValues(`uf`)
                }
            })
            return result?.data
        }
    });
    const { dataCidades }: ICidades = {
        dataCidades: data || []
    }

    useEffect(() => {
        if (open && isEdit) {
            const fazendaAtual = getMainFormValues(`dados_cadastrais.fazendas.${index}`);
            if (fazendaAtual) {
                reset({
                    ...fazendaAtual,
                    vencimento_contrato: fazendaAtual.vencimento_contrato
                        ? moment(fazendaAtual.vencimento_contrato, 'YYYY-MM-DD')
                        : undefined,
                });
            }
        } else if (open && !isEdit) {
            reset(defaultValuesFazenda);
        }
    }, [open, isEdit, index, getMainFormValues, reset]);

    //funções manipuladoras
    const handlePesquisarCep = async () => {
        const { cep } = getValues()
        if (cep === undefined || cep === "") return

        const unmaskCep = desformatarCEP(cep)

        if (unmaskCep.length !== 8) {
            enqueueSnackbar('CEP inválido. Deve conter 8 dígitos.', { variant: 'error' })
            return
        }

        try {
            const response = await axiosClient.get<ViaCEPResposta>(`https://viacep.com.br/ws/${unmaskCep}/json/`);
            const { localidade, uf } = response.data
            setValue(`uf`, uf)
            setValue(`cidade`, localidade)
        } catch (error) {
            console.error('Erro ao buscar endereço por CEP:', error)
            return
        }
    }

    const handleSave = handleSubmit((data) => {
        try {
            if (isEdit) {
                setMainFormValue(`dados_cadastrais.fazendas.${index}`, data);
                enqueueSnackbar('Fazenda atualizada com sucesso!', { variant: 'success' });
            } else {
                const fazendasAtuais = getMainFormValues('dados_cadastrais.fazendas') || [];
                setMainFormValue('dados_cadastrais.fazendas', [...fazendasAtuais, data]);
                enqueueSnackbar('Fazenda adicionada com sucesso!', { variant: 'success' });
            }
            onClose();
        } catch (error) {
            enqueueSnackbar('Erro ao salvar fazenda', { variant: 'error' });
            console.error('Erro ao salvar fazenda:', error);
        }
    }, (errors) => {
        enqueueSnackbar('Por favor, corrija os erros no formulário', { variant: 'error' });
    });
    //

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle><b>{isEdit ? 'Editar' : 'Adicionar'} Fazenda</b></DialogTitle>
            <DialogContent>
                <Grid container spacing={2} mb={2} mt={1}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name={`nome`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    {...field}
                                    required
                                    label="Nome da Fazenda"
                                    variant="outlined"
                                    fullWidth
                                    error={invalid}
                                    helperText={error?.message || ""}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name={`cnpj`}
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (value === undefined || value === "") return true;
                                    return validarCpfCnpj(value) || 'CNPJ inválido';
                                },
                            }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    label='CNPJ'
                                    type="text"
                                    fullWidth
                                    value={field.value}
                                    onChange={(e) => {
                                        const valor = e.target.value;
                                        field.onChange(formatarCNPJ(valor));
                                    }}
                                    slotProps={{ htmlInput: { maxLength: 16 } }}
                                    error={invalid}
                                    helperText={error ? error.message : ""}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Controller
                            name={`natureza_propriedade`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <FormControl fullWidth {...field} error={invalid} required>
                                    <InputLabel id="Natureza propriedade-select">Natureza propriedade</InputLabel>
                                    <Select
                                        labelId="Natureza propriedade-select"
                                        value={field.value}
                                        label="Natureza propriedade"
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {opcoesNatureza.map((natureza, index) => (
                                            <MenuItem key={index} value={natureza.value}>{natureza.label}</MenuItem>
                                        ))}
                                    </Select>
                                    {error && (
                                        <FormHelperText>{error?.message}</FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Controller
                            name={`incra`}
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    label='INCRA'
                                    type="text"
                                    fullWidth
                                    value={field.value}
                                    onChange={(e) => {
                                        const valor = e.target.value.slice(0, 13);
                                        field.onChange(formatarIncra(valor));
                                    }}
                                    slotProps={{ htmlInput: { maxLength: 13 } }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                        <Controller
                            name={`nirf`}
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (value === undefined || value === "") return true;
                                    return /^[0-9]{7}-[0-9]{1}$/.test(value) || 'NIRF inválido';
                                },
                            }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    label='NIRF'
                                    type="text"
                                    fullWidth
                                    value={field.value}
                                    onChange={(e) => {
                                        const valor = e.target.value.slice(0, 13);
                                        field.onChange(formatarNirf(valor));
                                    }}
                                    slotProps={{ htmlInput: { maxLength: 13 } }}
                                    error={invalid}
                                    helperText={error ? error.message : ""}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: possuiArendatarioComodatario ? 4 : 6 }} display="flex" justifyContent="flex-start">
                        <Controller
                            name={`arrendatario`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <FormControl {...field} required error={invalid}>
                                    <FormLabel id="arrendatario">Arrendatário/Comodatário?</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="arrendatario"
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {
                                            opcoesRadio.map((opcao, index) => (
                                                <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                            ))
                                        }
                                    </RadioGroup>
                                    {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>

                    {
                        possuiArendatarioComodatario &&
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Controller
                                name={`vencimento_contrato`}
                                control={control}
                                rules={{
                                    required: possuiArendatarioComodatario ? 'Campo obrigatório' : false,
                                    validate: (value) => {
                                        if (!value) return possuiArendatarioComodatario ? 'Campo obrigatório' : true;
                                        const selectedDate = moment(value);
                                        const today = moment().startOf('day');
                                        if (selectedDate.isBefore(today)) {
                                            return 'A data de vencimento não pode ser no passado';
                                        }
                                        return true;
                                    }
                                }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <DatePicker
                                        {...field}
                                        sx={{ width: '100%' }}
                                        label="Vencimento contrato"
                                        minDate={moment()}
                                        onChange={(data) => field.onChange(data as Moment)}
                                        slotProps={{
                                            textField: {
                                                error: invalid,
                                                helperText: error?.message || ""
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    }

                    <Grid size={{ xs: 12, md: possuiArendatarioComodatario ? 4 : 6 }}>
                        <Controller
                            name={`tamanho`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    {...field}
                                    required
                                    label="Tamanho (Ha)"
                                    variant="outlined"
                                    type="number"
                                    fullWidth
                                    error={invalid}
                                    helperText={error?.message || ""}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                        <Controller
                            name={`cep`}
                            rules={{ required: 'Campo obrigatório' }}
                            control={control}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    {...field}
                                    label="CEP"
                                    variant="outlined"
                                    defaultValue={""}
                                    fullWidth
                                    required
                                    onChange={(e) => field.onChange(formatarCEP(e.target.value))}
                                    onBlur={handlePesquisarCep}
                                    error={invalid}
                                    helperText={error?.message || ""}
                                    slotProps={{
                                        input: {
                                            endAdornment: <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handlePesquisarCep}
                                                    edge="end"
                                                >
                                                    <SearchIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                        <Controller
                            name={`uf`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <FormControl fullWidth {...field} error={invalid} required>
                                    <InputLabel id="uf-select">UF</InputLabel>
                                    <Select
                                        labelId="uf-select"
                                        value={field.value}
                                        label="UF"
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {ufs.map((uf, index) => (
                                            <MenuItem key={index} value={uf}>{uf}</MenuItem>
                                        ))}
                                    </Select>
                                    {error && (
                                        <FormHelperText>{error?.message}</FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 4, md: 6 }}>
                        <Controller
                            name={`cidade`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <>
                                    <Autocomplete
                                        {...field}
                                        fullWidth
                                        disablePortal
                                        options={dataCidades}
                                        defaultValue={""}
                                        onChange={(_, v) => field.onChange(v)}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                label="Cidade"
                                                required
                                                defaultValue={""}
                                                error={invalid}
                                                helperText={error?.message || ""}
                                            />
                                        }
                                    />
                                </>
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Controller
                            name={`inscricao_fazenda`}
                            control={control}
                            rules={{
                                required: 'Campo obrigatório',
                                validate: async (value) => {
                                    if (!value) return "Campo obrigatório";

                                    const uf = getValues(`uf`);
                                    if (!uf || uf === "") {
                                        return "Selecione o UF antes de preencher a inscrição estadual";
                                    }

                                    try {
                                        const response = await axiosClient.post(`/cadastros/verificar-inscricao-estadual`, {
                                            inscricao_estadual: desformatarCPFCNPJ(value),
                                            uf: uf,
                                        });

                                        if (response.status === 200) {
                                            return true;
                                        }
                                        return response.data?.mensagem || 'Inscrição estadual inválida!';
                                    } catch (error: any) {
                                        console.error('Erro ao validar inscrição estadual:', error);
                                        if (error.response?.data?.mensagem) {
                                            return error.response.data.mensagem;
                                        }
                                        return 'Erro ao validar inscrição estadual. Verifique os dados informados.';
                                    }
                                }
                            }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <TextField
                                    required
                                    label='Inscrição Fazenda'
                                    type="text"
                                    fullWidth
                                    value={field.value}
                                    onChange={(e) => field.onChange((e.target.value))}
                                    error={invalid}
                                    helperText={error ? error.message : ""}
                                />
                            )}
                        />
                    </Grid>

                    <Grid container size={{ xs: 12 }}>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                                name={`funrural`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <FormControl {...field} required error={invalid}>
                                        <FormLabel id="funrural">Funrural?</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-labelledby="funrural"
                                            value={field.value || "S"}
                                            defaultValue={"S"}
                                            onChange={(e) => {
                                                const valor = e.target.value;
                                                field.onChange(valor);
                                            }}
                                        >
                                            {
                                                opcoesRadio.map((opcao, index) => (
                                                    <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                                ))
                                            }
                                        </RadioGroup>
                                        {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                                name={`senar`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <FormControl {...field} required error={invalid}>
                                        <FormLabel id="senar">Desconta SENAR?</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-labelledby="senar"
                                            value={field.value || "S"}
                                            defaultValue={"S"}
                                            onChange={(e) => {
                                                const valor = e.target.value;
                                                field.onChange(valor);
                                            }}
                                        >
                                            {
                                                opcoesRadio.map((opcao, index) => (
                                                    <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                                ))
                                            }
                                        </RadioGroup>
                                        {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                                name={`rat`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <FormControl {...field} required error={invalid}>
                                        <FormLabel id="rat">Desconta RAT?</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-labelledby="rat"
                                            value={field.value || "S"}
                                            defaultValue={"S"}
                                            onChange={(e) => {
                                                const valor = e.target.value;
                                                field.onChange(valor);
                                            }}
                                        >
                                            {
                                                opcoesRadio.map((opcao, index) => (
                                                    <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                                ))
                                            }
                                        </RadioGroup>
                                        {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 3 }}>
                            <Controller
                                name={`tributa_icms`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <FormControl {...field} required error={invalid}>
                                        <FormLabel id="tributa_icms">Tributa ICMS Leite?</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-labelledby="tributa_icms"
                                            value={field.value || "N"}
                                            defaultValue={"N"}
                                            onChange={(e) => {
                                                const valor = e.target.value;
                                                setValue(`icms_aliquota`, "");
                                                setValue(`icms_percentual`, "");
                                                field.onChange(valor);
                                            }}
                                        >
                                            {
                                                opcoesRadio.map((opcao, index) => (
                                                    <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                                ))
                                            }
                                        </RadioGroup>
                                        {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Grid>
                    </Grid>
                    {
                        tributaIcms &&
                        <>
                            <Grid size={{ xs: 4 }} display="flex" justifyContent="flex-start">
                                <Controller
                                    name={`trib_entrada_leite`}
                                    control={control}
                                    rules={{ required: 'Campo obrigatório' }}
                                    render={({ field, fieldState: { error, invalid } }) => (
                                        <TextField
                                            {...field}
                                            required
                                            label='Tributação Entrada de Leite (%)'
                                            type="number"
                                            fullWidth
                                            value={field.value}
                                            onChange={(e) => field.onChange((e.target.value))}
                                            error={invalid}
                                            helperText={error ? error.message : ""}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 4 }} display="flex" justifyContent="flex-start">
                                <Controller
                                    name={`icms_aliquota`}
                                    control={control}
                                    rules={{ required: 'Campo obrigatório' }}
                                    render={({ field, fieldState: { error, invalid } }) => (
                                        <TextField
                                            {...field}
                                            required
                                            label='Aliquota ICMS Leite (%)'
                                            type="number"
                                            fullWidth
                                            value={field.value}
                                            onChange={(e) => field.onChange((e.target.value))}
                                            error={invalid}
                                            helperText={error ? error.message : ""}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 4 }} display="flex" justifyContent="flex-start">
                                <Controller
                                    name={`icms_percentual`}
                                    control={control}
                                    rules={{ required: 'Campo obrigatório' }}
                                    render={({ field, fieldState: { error, invalid } }) => (
                                        <TextField
                                            {...field}
                                            required
                                            label='Incentivo ICMS (%)'
                                            type="number"
                                            fullWidth
                                            value={field.value}
                                            onChange={(e) => field.onChange((e.target.value))}
                                            error={invalid}
                                            helperText={error ? error.message : ""}
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    }
                    <Grid size={{ xs: 6, md: 3 }}>
                        <Controller
                            name={`produtor_leite`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <FormControl {...field} required error={invalid}>
                                    <FormLabel id="produtor_leite">Produtor de Leite?</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="Produtor de Leite?"
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {
                                            opcoesRadio.map((opcao, index) => (
                                                <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                            ))
                                        }
                                    </RadioGroup>
                                    {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        <Controller
                            name={`produtor_cafe`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <FormControl {...field} required error={invalid}>
                                    <FormLabel id="produtor_cafe">Produtor de Café?</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="Produtor de Café?"
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {
                                            opcoesRadio.map((opcao, index) => (
                                                <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                            ))
                                        }
                                    </RadioGroup>
                                    {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />

                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        <Controller
                            name={`produtor_milho`}
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <FormControl {...field} required error={invalid}>
                                    <FormLabel id="produtor_milho">Produtor de Milho?</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="Produtor de Milho?"
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {
                                            opcoesRadio.map((opcao, index) => (
                                                <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                            ))
                                        }
                                    </RadioGroup>
                                    {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>
                </Grid>

            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    )
}


interface DialogAddEditFazendaProps {
    open: boolean;
    index?: number;
    onClose: () => void;
}

interface IFazendaForm {
    nome: string;
    natureza_propriedade: string;
    incra: string;
    nirf: string;
    arrendatario: string;
    vencimento_contrato?: Moment;
    tamanho: string;
    funrural: string;
    senar: string;
    rat: string;
    tributa_icms: string;
    trib_entrada_leite?: string;
    icms_aliquota?: string;
    icms_percentual?: string;
    cep: string;
    uf: string;
    cidade: string;
    inscricao_fazenda: string;
    vencimento_inscricao?: Moment;
    produtor_leite: string;
    produtor_cafe: string;
    produtor_milho: string;
    cnpj: string;
}

const defaultValuesFazenda: IFazendaForm = {
    nome: '',
    cnpj: '',
    natureza_propriedade: '',
    incra: '',
    nirf: '',
    arrendatario: 'Não',
    tamanho: '',
    funrural: 'Sim',
    senar: 'Sim',
    rat: 'Sim',
    tributa_icms: 'Não',
    cidade: '',
    uf: '',
    cep: '',
    inscricao_fazenda: '',
    produtor_leite: 'Não',
    produtor_cafe: 'Não',
    produtor_milho: 'Não'
}

interface ICidades {
    dataCidades: Array<string>
}
